import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllContactSubmissions,
  getAllUserFeedback,
  getAllWaitlistUsers
} from "../../api/index";

const Admin = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("contacts");

  const [emails, setEmails] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [waitlist, setWaitlist] = useState([]);

  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  useEffect(() => {
    const admin = localStorage.getItem("admin_user");

    if (!admin) {
      navigate("/");
      return;
    }

    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    const contactsRes = await getAllContactSubmissions();
    const feedbackRes = await getAllUserFeedback();
    const waitlistRes = await getAllWaitlistUsers();

    if (contactsRes.success) setEmails(contactsRes.data);
    if (feedbackRes.success) setFeedback(feedbackRes.data);
    if (waitlistRes.success) setWaitlist(waitlistRes.data);

    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_user");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">

      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Quzy Admin Dashboard</h1>

        <button
          onClick={handleLogout}
          className="bg-red-500 text-white px-4 py-2 rounded-lg"
        >
          Logout
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6">
        {["contacts","feedback","waitlist"].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg ${
              activeTab === tab
                ? "bg-indigo-600 text-white"
                : "bg-white border"
            }`}
          >
            {tab === "contacts" && "Contact Messages"}
            {tab === "feedback" && "User Feedback"}
            {tab === "waitlist" && "Waitlist"}
          </button>
        ))}
      </div>

      {/* CONTACT SUBMISSIONS */}
      {activeTab === "contacts" && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">

          {loading ? (
            <div className="p-8 text-center">Loading...</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-gray-600 text-sm">
                  <th className="p-4">Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {emails.map(email => (
                  <tr key={email.id} className="border-b hover:bg-gray-50">
                    <td className="p-4 font-medium">{email.name}</td>
                    <td className="text-blue-600">{email.email}</td>
                    <td>{email.subject}</td>

                    <td>
                      <span className="px-3 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">
                        {email.status}
                      </span>
                    </td>

                    <td>{new Date(email.created_at).toLocaleDateString()}</td>

                    <td>
                      <button
                        onClick={() => setSelectedEmail(email)}
                        className="text-indigo-600 hover:underline"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* USER FEEDBACK */}
      {activeTab === "feedback" && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">

          <table className="w-full">

            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-gray-600 text-sm">
                <th className="p-4">Type</th>
                <th>Email</th>
                <th>Device</th>
                <th>Status</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {feedback.map(item => (
                <tr key={item.id} className="border-b hover:bg-gray-50">

                  <td className="p-4">{item.feedback_type}</td>

                  <td>{item.email || "-"}</td>

                  <td>
                    {item.device_model} ({item.os_version})
                  </td>

                  <td>
                    <span className="px-3 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                      {item.status}
                    </span>
                  </td>

                  <td>
                    {new Date(item.created_at).toLocaleDateString()}
                  </td>

                  <td>
                    <button
                      onClick={() => setSelectedFeedback(item)}
                      className="text-indigo-600 hover:underline"
                    >
                      View
                    </button>
                  </td>

                </tr>
              ))}
            </tbody>

          </table>
        </div>
      )}

      {/* WAITLIST */}
      {activeTab === "waitlist" && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">

          <table className="w-full">

            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-gray-600 text-sm">
                <th className="p-4">Email</th>
                <th>Name</th>
                <th>Referral</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>

            <tbody>
              {waitlist.map(user => (
                <tr key={user.id} className="border-b hover:bg-gray-50">

                  <td className="p-4 text-blue-600">{user.email}</td>

                  <td>{user.name || "-"}</td>

                  <td>{user.referral_source || "-"}</td>

                  <td>
                    <span className="px-3 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                      {user.status}
                    </span>
                  </td>

                  <td>
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>

                </tr>
              ))}
            </tbody>

          </table>

        </div>
      )}

      {/* CONTACT MODAL */}
      {selectedEmail && (
        <Modal
          title={selectedEmail.subject}
          onClose={() => setSelectedEmail(null)}
        >
          <p className="text-sm text-gray-500 mb-2">
            {selectedEmail.name} ({selectedEmail.email})
          </p>

          <p className="whitespace-pre-line">
            {selectedEmail.message}
          </p>
        </Modal>
      )}

      {/* FEEDBACK MODAL */}
      {selectedFeedback && (
        <Modal
          title={`Feedback - ${selectedFeedback.feedback_type}`}
          onClose={() => setSelectedFeedback(null)}
        >
          <p className="text-sm text-gray-500 mb-2">
            {selectedFeedback.email || "Anonymous"}
          </p>

          <p className="mb-4 whitespace-pre-line">
            {selectedFeedback.message}
          </p>

          {selectedFeedback.screenshot_url && (
            <img
              src={selectedFeedback.screenshot_url}
              className="rounded-lg"
            />
          )}
        </Modal>
      )}

    </div>
  );
};

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-6">
    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
      <h2 className="text-xl font-bold mb-4">{title}</h2>
      {children}

      <div className="flex justify-end mt-6">
        <button
          onClick={onClose}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

export default Admin;